// Reveal-mechanic A/B assignment (PREREG_reveal_ab.md). Read once at session start and
// stamped onto the consent record + every trace, like sourceTag / retroConfig.
//
//   arm A  = as-is. Puzzle hidden while silent, revealed by talking (current behaviour).
//   arm B  = puzzle always visible + a neutral, non-contingent "Keep talking." after 8s.
//
// TWO FIELDS ARE STAMPED, and the difference matters:
//   revealArm     what the user was ASSIGNED (sticky, per person, across all labs games)
//   revealRegime  what they actually EXPERIENCED on this trace
// They diverge while arm B is live in some games but not others (see REVEAL_B_LIVE). A
// B-assigned user playing the 24-game during the rebus-only rollout gets
// revealArm:'B', revealRegime:'A'. Analysis filters on REGIME; the pair also makes
// mixed-exposure users detectable instead of invisible.
//
// ASSIGNMENT is resolved in this order, and the order is the whole design:
//   1. ?reveal=A|B      QA override. Cached, and flagged `forced` so QA traffic is filterable.
//   2. localStorage      an existing assignment ALWAYS wins -> perfect stickiness. A change to
//                        REVEAL_A_SHARE therefore moves nobody who already has an arm; it only
//                        affects users seen for the first time after the change.
//   3. invite token      a stable uniform quantile from the ?t= HMAC. Same answer on every
//                        device, so the email-on-phone -> play-on-laptop hop keeps one arm
//                        without needing the arm on the recruitment link.
//   4. random            organic users with no token.
//
// DIRECTION: we assign A when p < threshold, so RAISING arm A's share never reassigns an
// already-resolved user (monotone). That is the direction we expect to need: arm B traces are
// excluded from the Tier-1 confirmatory set, so 50/50 halves the arm-A stream that feeds drift
// monitoring, the per-problem correlation and Tier-2 volume. Lowering A's share would move a
// slice of users - detectable, because the threshold in force is stamped on every trace.

export type RevealArm = 'A' | 'B'

export type RevealAssignment = {
  arm: RevealArm
  threshold: number // REVEAL_A_SHARE in force when this user was resolved
  forced: boolean // came from ?reveal= (QA), not from randomisation
}

/** Share of users assigned to arm A. 0.5 = the registered 50/50. */
export const REVEAL_A_SHARE = 0.5

/**
 * Games where arm B is actually IMPLEMENTED. Until a game appears here every player
 * experiences arm A in it regardless of assignment, and their traces carry
 * revealRegime:'A'. Rollout order is deliberate (rebus first, 24-game once DV1 closes).
 */
export const REVEAL_B_LIVE: ReadonlyArray<string> = ['rebus']

const KEY = 'nvRevealArm'

/** FNV-1a over the token -> a stable uniform quantile in [0,1). */
const quantile = (token: string): number => {
  let h = 0x811c9dc5
  for (let i = 0; i < token.length; i++) {
    h ^= token.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h / 0x100000000
}

const armFor = (p: number): RevealArm => (p < REVEAL_A_SHARE ? 'A' : 'B')

const read = (): RevealAssignment | null => {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const v = JSON.parse(raw)
    if (v && (v.arm === 'A' || v.arm === 'B') && typeof v.threshold === 'number') {
      return { arm: v.arm, threshold: v.threshold, forced: !!v.forced }
    }
  } catch {
    /* private mode / disabled storage — fall through to a fresh resolve */
  }
  return null
}

const write = (a: RevealAssignment): RevealAssignment => {
  try {
    localStorage.setItem(KEY, JSON.stringify(a))
  } catch {
    /* not fatal: an unpersisted assignment still works for this session */
  }
  return a
}

/**
 * Resolve this user's arm. `token` is the ?t= invite token from getSourceTag(); pass null
 * for organic sessions. Safe to call repeatedly — the first call fixes the assignment.
 */
export function getRevealAssignment(token: string | null): RevealAssignment {
  let forcedArm: RevealArm | null = null
  try {
    const q = new URLSearchParams(window.location.search).get('reveal')
    if (q === 'A' || q === 'B') forcedArm = q
  } catch {
    /* no window/search — treat as no override */
  }
  if (forcedArm) return write({ arm: forcedArm, threshold: REVEAL_A_SHARE, forced: true })

  const cached = read()
  if (cached) return cached

  if (token) return write({ arm: armFor(quantile(token)), threshold: REVEAL_A_SHARE, forced: false })
  return write({ arm: armFor(Math.random()), threshold: REVEAL_A_SHARE, forced: false })
}

/** What the player actually experiences in `game`, given their assignment. */
export const revealRegimeFor = (game: string, arm: RevealArm): RevealArm =>
  REVEAL_B_LIVE.includes(game) ? arm : 'A'

/** Silence before arm B's neutral reminder. Registered value — do not tune without a log entry. */
export const REVEAL_B_NUDGE_MS = 8000
